import { useEffect } from 'react'
import CalendarWidget from '../../calendar/widget'
import { CalendarWidgetConfig } from '../../calendar/config'
import type { BookingTranslationKey } from '../config'
import { Dialog, DialogContent } from '../../../components/ui/dialog'
import { useBookingCss } from '../hooks/useBookingCss'

interface CalendarModalProps {
  calendarConfig: CalendarWidgetConfig
  calendarWidgetId: string
  feedId: number
  resolvedTheme?: 'light' | 'dark'
  initialCheckin: string | null
  initialCheckout: string | null
  visible: boolean
  onClose: () => void
  /** #1424: this modal's own chrome text (title, header, footer buttons). */
  _t: (key: BookingTranslationKey, defaultValue?: string) => string
}

export default function CalendarModal({
  calendarConfig,
  calendarWidgetId,
  feedId,
  resolvedTheme,
  initialCheckin,
  initialCheckout,
  visible,
  onClose,
  _t,
}: CalendarModalProps) {
  // Sync dates into the calendar each time the modal becomes visible
  useEffect(() => {
    if (visible && (initialCheckin || initialCheckout)) {
      const timer = setTimeout(() => {
        window.dispatchEvent(new CustomEvent(`calendar-set-dates-${calendarWidgetId}`, {
          detail: { checkin: initialCheckin, checkout: initialCheckout },
        }))
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [visible, initialCheckin, initialCheckout, calendarWidgetId])

  const cssString = useBookingCss()

  // Clear from the modal chrome (#602) without touching the calendar widget's
  // own UI: null set-dates clears the embedded calendar, whose state-change
  // broadcast then clears the booking form; the explicit dates-changed
  // dispatch covers listeners even if the calendar isn't mounted yet.
  const handleClearDates = () => {
    window.dispatchEvent(new CustomEvent(`calendar-set-dates-${calendarWidgetId}`, {
      detail: { checkin: null, checkout: null },
    }))
    window.dispatchEvent(new CustomEvent(`calendar-dates-changed-${calendarWidgetId}`, {
      detail: { checkin: null, checkout: null },
    }))
  }

  return (
    <Dialog open={visible} onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className={`booking-modal-content booking-calendar-modal scrollbar-hide ${resolvedTheme === 'dark' ? 'dark' : ''}`} style={{ width: 'auto' }} elevateDom cssString={cssString} title={_t('calendar_modal_title')}>
        {/* Modal header + footer chrome matching the WP plugin (#602) */}
        <div className="booking-calendar-modal-header">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          <span>{_t('label_when')}</span>
        </div>
        <div className="booking-calendar-modal-body">
          <CalendarWidget
            options={calendarConfig}
            feedId={feedId}
            widgetId={calendarWidgetId}
            widgetType="calendar"
            resolvedTheme={resolvedTheme}
            data={undefined!}
          />
        </div>
        <div className="booking-calendar-modal-footer">
          <button type="button" className="booking-calendar-clear-btn" onClick={handleClearDates}>{_t('button_clear_dates')}</button>
          <button type="button" className="booking-calendar-close-btn" onClick={onClose}>{_t('button_close')}</button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
